import React, { useState, useCallback, useEffect } from 'react';

const WythoffGame = () => {
  const [numbersVisible, setNumbersVisible] = useState(false);
  const [mode, setMode] = useState('analysis'); // 'analysis' or 'game'
  const [cellColors, setCellColors] = useState({});
  const [selectedColor, setSelectedColor] = useState('#ffb3ba');
  const [hoveredCell, setHoveredCell] = useState(null);
  const [highlightedCells, setHighlightedCells] = useState(new Set());
  const [winningPositions, setWinningPositions] = useState(new Set());
  const [flagMode, setFlagMode] = useState(false);
  
  // 游戏模式状态
  const [gameState, setGameState] = useState({
    currentPosition: { row: 10, col: 20 }, // A堆10枚，B堆20枚
    currentPlayer: 1,
    gameOver: false,
    winner: null,
    validMoves: new Set()
  });

  const colors = [
    { color: '#ffb3ba', name: '粉红' },
    { color: '#bae1ff', name: '天蓝' },
    { color: '#baffc9', name: '薄荷绿' },
    { color: '#ffffba', name: '淡黄' },
    { color: '#ffdfba', name: '橙色' },
    { color: '#e0bbff', name: '薰衣草' },
    { color: '#ffb3e6', name: '洋红' },
    { color: '#c9c9c9', name: '灰色' },
  ];

  const gridSize = { rows: 11, cols: 21 };

  // 计算有效移动
  const calculateValidMoves = useCallback((row, col) => {
    const moves = new Set();
    
    // 向左移动（减少B堆）
    for (let c = 0; c < col; c++) {
      moves.add(`${row}-${c}`);
    }
    
    // 向下移动（减少A堆）
    for (let r = 0; r < row; r++) {
      moves.add(`${r}-${col}`);
    }
    
    // 向左下移动（同时减少A堆和B堆相同数量）
    const maxDiagonal = Math.min(row, col);
    for (let i = 1; i <= maxDiagonal; i++) {
      moves.add(`${row - i}-${col - i}`);
    }
    
    return moves;
  }, []);

  // 初始化游戏
  const initializeGame = useCallback(() => {
    const initialPos = { row: 10, col: 20 };
    const validMoves = calculateValidMoves(initialPos.row, initialPos.col);
    
    setGameState({
      currentPosition: initialPos,
      currentPlayer: 1,
      gameOver: false,
      winner: null,
      validMoves
    });
    setCellColors({});
  }, [calculateValidMoves]);

  // 当切换到游戏模式时初始化
  useEffect(() => {
    if (mode === 'game') {
      initializeGame();
    }
  }, [mode, initializeGame]);

  const handleCellClick = useCallback((row, col, event) => {
    if (mode === 'game') {
      // 游戏模式下的点击处理
      const key = `${row}-${col}`;
      
      if (gameState.gameOver) return;
      
      if (!gameState.validMoves.has(key)) {
        // 无效移动
        return;
      }
      
      // 执行移动
      const newValidMoves = calculateValidMoves(row, col);
      const isGameOver = row === 0 && col === 0;
      
      setGameState(prev => ({
        ...prev,
        currentPosition: { row, col },
        currentPlayer: prev.currentPlayer === 1 ? 2 : 1,
        gameOver: isGameOver,
        winner: isGameOver ? prev.currentPlayer : null,
        validMoves: newValidMoves
      }));
      
    } else {
      // 分析模式下的点击处理
      if (event.type === 'click') {
        const key = `${row}-${col}`;
        
        if (flagMode) {
          // 旗帜模式：添加或移除必胜点标记
          const newWinningPositions = new Set(winningPositions);
          if (newWinningPositions.has(key)) {
            newWinningPositions.delete(key);
          } else {
            newWinningPositions.add(key);
          }
          setWinningPositions(newWinningPositions);
        } else {
          // 颜色标记模式：标记危险区域（向右上方向）
          const newCellColors = { ...cellColors };

          for (let r = 0; r <= gridSize.rows - 1; r++) {
            for (let c = 0; c <= gridSize.cols - 1; c++) {
              const cellKey = `${r}-${c}`;
              if ((r >= row && c === col) || 
                  (c >= col && r === row) || 
                  (r - c === row - col && r >= row && c >= col)) {
                newCellColors[cellKey] = selectedColor;
              }
            }
          }

          setCellColors(newCellColors);
        }
      }
    }
  }, [mode, selectedColor, cellColors, gridSize, gameState, calculateValidMoves]);

  const handleCellRightClick = useCallback((row, col, event) => {
    event.preventDefault();
    const key = `${row}-${col}`;
    
    // 优先移除旗帜标记
    if (winningPositions.has(key)) {
      const newWinningPositions = new Set(winningPositions);
      newWinningPositions.delete(key);
      setWinningPositions(newWinningPositions);
    } else if (mode === 'analysis') {
      // 移除颜色标记（仅分析模式）
      const newCellColors = { ...cellColors };
      delete newCellColors[key];
      setCellColors(newCellColors);
    }
  }, [cellColors, mode, winningPositions]);

  const handleCellHover = useCallback((row, col, isEntering) => {
    if (isEntering) {
      setHoveredCell({ row, col });
      
      if (mode === 'analysis') {
        const highlighted = new Set();
        
        for (let r = 0; r <= gridSize.rows - 1; r++) {
          for (let c = 0; c <= gridSize.cols - 1; c++) {
            if ((r <= row && c === col) || 
                (c <= col && r === row) || 
                (r - c === row - col && r <= row && c <= col)) {
              highlighted.add(`${r}-${c}`);
            }
          }
        }
        setHighlightedCells(highlighted);
      }
    } else {
      setHoveredCell(null);
      setHighlightedCells(new Set());
    }
  }, [mode, gridSize]);

  const clearAllColors = () => {
    setCellColors({});
  };

  const clearAllFlags = () => {
    setWinningPositions(new Set());
  };

  const clearAllMarks = () => {
    setCellColors({});
    setWinningPositions(new Set());
  };

  // 计算Wythoff博弈的所有必胜点(P点)
  const calculateWinningPositions = useCallback(() => {
    const phi = (1 + Math.sqrt(5)) / 2; // 黄金分割比 ≈ 1.618
    const winningPos = new Set();
    
    for (let row = 0; row < gridSize.rows; row++) {
      for (let col = 0; col < gridSize.cols; col++) {
        const diff = Math.abs(row - col);
        const minVal = Math.min(row, col);
        
        // Wythoff博弈必胜点的判断条件：floor(|a-b| * φ) = min(a,b)
        if (Math.floor(diff * phi) === minVal) {
          winningPos.add(`${row}-${col}`);
        }
      }
    }
    
    return winningPos;
  }, [gridSize]);

  // 自动标记必胜点
  const autoMarkWinningPositions = useCallback(() => {
    const winningPos = calculateWinningPositions();
    setWinningPositions(winningPos);
  }, [calculateWinningPositions]);

  // 渲染单个硬币
  const CoinComponent = ({ index, type = 'gold' }) => {
    const delay = index * 50; // 动画延迟
    return (
      <div
        className={`relative w-6 h-6 rounded-full shadow-lg transform transition-all duration-300 hover:scale-110`}
        style={{
          background: type === 'gold' 
            ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 50%, #ffc107 100%)'
            : 'linear-gradient(135deg, #e8e8e8 0%, #f5f5f5 50%, #d0d0d0 100%)',
          border: type === 'gold' ? '2px solid #b8860b' : '2px solid #999',
          animationDelay: `${delay}ms`
        }}
      >
        {/* 硬币表面光泽效果 */}
        <div 
          className="absolute top-1 left-1 w-2 h-2 rounded-full opacity-60"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
          }}
        />
        {/* 硬币上的数字或符号 */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xs font-bold ${type === 'gold' ? 'text-amber-800' : 'text-gray-600'}`}>
            {type === 'gold' ? '$' : '¢'}
          </span>
        </div>
      </div>
    );
  };

  // 渲染硬币堆
  const renderCoins = (count, label, type = 'gold') => {
    if (count === 0) {
      return (
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
          <div className="h-20 flex items-center justify-center">
            <span className="text-gray-400 text-sm">空堆</span>
          </div>
          <div className="text-xs text-gray-600 mt-1 text-center">0 枚</div>
        </div>
      );
    }

    const coins = [];
    const coinsPerRow = 8;
    const rows = Math.ceil(count / coinsPerRow);
    
    // 创建硬币，支持堆叠效果
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / coinsPerRow);
      const col = i % coinsPerRow;
      const stackHeight = Math.floor(i / (coinsPerRow * 3)); // 每3排增加一层高度
      
      coins.push(
        <div
          key={i}
          className="absolute transition-all duration-500 ease-out"
          style={{
            left: `${col * 28 + (row % 2) * 14}px`, // 交错排列
            top: `${row * 24 - stackHeight * 2}px`,
            zIndex: count - i,
            transform: `translateZ(${stackHeight * 2}px)`,
          }}
        >
          <CoinComponent index={i} type={type} />
        </div>
      );
    }
    
    const containerHeight = Math.max(80, rows * 24 + 20);
    const containerWidth = Math.max(200, coinsPerRow * 28 + 40);
    
    return (
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-lg border shadow-sm">
        <div className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <span>{label}</span>
          <div className={`w-3 h-3 rounded-full ${type === 'gold' ? 'bg-yellow-400' : 'bg-gray-400'}`} />
        </div>
        <div 
          className="relative bg-white rounded-lg border-2 border-dashed border-amber-200 p-3 overflow-hidden"
          style={{
            width: `${containerWidth}px`,
            height: `${containerHeight}px`,
            perspective: '1000px'
          }}
        >
          {coins}
          
          {/* 底部阴影效果 */}
          <div 
            className="absolute bottom-2 left-1/2 transform -translate-x-1/2 opacity-20"
            style={{
              width: `${Math.min(count * 4, containerWidth - 20)}px`,
              height: '4px',
              background: 'radial-gradient(ellipse, #333 0%, transparent 70%)',
              filter: 'blur(2px)'
            }}
          />
        </div>
        <div className="text-xs text-gray-600 mt-2 text-center font-medium">
          {count} 枚硬币
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-2 text-center">
          Wythoff's Game
        </h1>
        <p className="text-center text-gray-600 mb-6">威索夫博弈 - 策略分析与实战</p>
        
        <div className="flex gap-6">
          {/* 主游戏区域 */}
          <div className="flex-1">
            {/* 控制面板 */}
            <div className="bg-white p-4 rounded-lg shadow-md mb-4 border">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode('analysis')}
                    className={`px-4 py-2 rounded transition-all ${
                      mode === 'analysis' 
                        ? 'bg-blue-500 text-white shadow-md' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📊 分析模式
                  </button>
                  
                  <button
                    onClick={() => setMode('game')}
                    className={`px-4 py-2 rounded transition-all ${
                      mode === 'game' 
                        ? 'bg-green-500 text-white shadow-md' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🎮 游戏模式
                  </button>
                </div>

                <button
                  onClick={() => setNumbersVisible(!numbersVisible)}
                  className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                >
                  {numbersVisible ? '隐藏坐标' : '显示坐标'}
                </button>

                <button
                  onClick={() => setFlagMode(!flagMode)}
                  className={`px-4 py-2 rounded transition-all border-2 ${
                    flagMode 
                      ? 'bg-yellow-500 text-white border-yellow-600 shadow-md' 
                      : 'bg-white text-yellow-600 border-yellow-500 hover:bg-yellow-50'
                  }`}
                >
                  🚩 {flagMode ? '旗帜模式' : '标记模式'}
                </button>

                {mode === 'analysis' && (
                  <>
                    <button
                      onClick={clearAllColors}
                      className="px-3 py-2 bg-red-400 text-white rounded text-sm hover:bg-red-500 transition-colors"
                    >
                      清除颜色
                    </button>
                  </>
                )}

                <button
                  onClick={clearAllFlags}
                  className="px-3 py-2 bg-orange-400 text-white rounded text-sm hover:bg-orange-500 transition-colors"
                >
                  清除旗帜
                </button>

                <button
                  onClick={autoMarkWinningPositions}
                  className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors flex items-center gap-1"
                >
                  ✨ 自动标记必胜点
                </button>

                <button
                  onClick={clearAllMarks}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  清除全部
                </button>

                {mode === 'game' && (
                  <button
                    onClick={initializeGame}
                    className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                  >
                    🔄 重新开始
                  </button>
                )}
              </div>

              {/* 游戏状态显示 */}
              {mode === 'game' && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="font-medium">
                        当前位置: ({gameState.currentPosition.row}, {gameState.currentPosition.col})
                      </span>
                      {!gameState.gameOver && (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          gameState.currentPlayer === 1 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          玩家 {gameState.currentPlayer} 的回合
                        </span>
                      )}
                    </div>
                    
                    {gameState.gameOver && (
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🎉</span>
                        <span className="font-bold text-green-700">
                          玩家 {gameState.winner} 获胜！
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 颜色选择器（仅分析模式且非旗帜模式） */}
              {mode === 'analysis' && !flagMode && (
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                  <span className="text-sm font-medium text-gray-700 mr-2">标记颜色:</span>
                  {colors.map(({ color, name }) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                        selectedColor === color ? 'border-gray-800 ring-2 ring-gray-400' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={name}
                    />
                  ))}
                </div>
              )}

              {/* 旗帜模式说明 */}
              {flagMode && (
                <div className="pt-3 border-t">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <span className="text-lg">🚩</span>
                      <span className="font-medium">旗帜标记模式</span>
                    </div>
                    <p className="text-sm text-yellow-700 mt-1">
                      点击任意格子放置/移除旗帜，用于标记重要位置（适用于所有模式）
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 游戏网格 */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
              <div 
                className="grid gap-1 mx-auto w-fit"
                style={{ 
                  gridTemplateColumns: `repeat(${gridSize.cols}, 32px)`,
                  gridTemplateRows: `repeat(${gridSize.rows}, 32px)`
                }}
              >
                {Array.from({ length: gridSize.rows }, (_, row) =>
                  Array.from({ length: gridSize.cols }, (_, col) => {
                    const actualRow = gridSize.rows - 1 - row;
                    const key = `${actualRow}-${col}`;
                    const isHighlighted = highlightedCells.has(key);
                    const cellColor = cellColors[key];
                    const isCurrentPosition = mode === 'game' && 
                      gameState.currentPosition.row === actualRow && 
                      gameState.currentPosition.col === col;
                    const isValidMove = mode === 'game' && gameState.validMoves.has(key);
                    const hasFlag = winningPositions.has(key);
                    
                    return (
                      <div
                        key={key}
                        className={`
                          relative border cursor-pointer transition-all duration-200
                          ${isCurrentPosition ? 'ring-4 ring-yellow-400 ring-opacity-80 border-yellow-500' : 'border-gray-300'}
                          ${isHighlighted ? 'ring-2 ring-blue-400 ring-opacity-60' : ''}
                          ${isValidMove ? 'hover:bg-green-100 border-green-400' : 'hover:border-gray-400'}
                          ${mode === 'game' && !isValidMove && !isCurrentPosition ? 'opacity-60' : ''}
                        `}
                        style={{
                          backgroundColor: cellColor || 
                            (isCurrentPosition ? '#fef3c7' : 
                             isHighlighted ? '#f0f8ff' : 
                             isValidMove ? '#f0fdf4' : 'white'),
                        }}
                        onClick={(e) => handleCellClick(actualRow, col, e)}
                        onContextMenu={(e) => handleCellRightClick(actualRow, col, e)}
                        onMouseEnter={() => handleCellHover(actualRow, col, true)}
                        onMouseLeave={() => handleCellHover(actualRow, col, false)}
                        title={`A堆: ${actualRow}枚, B堆: ${col}枚${isValidMove ? ' (可移动)' : ''}${hasFlag ? ' 🚩必胜点' : ''}`}
                      >
                        {/* 旗帜标记 */}
                        {hasFlag && (
                          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                            <div className="relative">
                              {/* 旗杆 */}
                              <div className="w-0.5 h-5 bg-amber-900 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                              {/* 旗帜 */}
                              <div 
                                className="w-3 h-2 absolute left-1/2 top-1/2 transform -translate-x-1/4 -translate-y-3/4 animate-pulse shadow-sm"
                                style={{
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  clipPath: 'polygon(0 0, 100% 0, 85% 50%, 100% 100%, 0 100%)'
                                }}
                              >
                              </div>
                              {/* 旗帜顶部高光 */}
                              <div 
                                className="w-3 h-1 absolute left-1/2 top-1/2 transform -translate-x-1/4 -translate-y-full opacity-40"
                                style={{
                                  background: 'linear-gradient(135deg, #fca5a5 0%, #ef4444 100%)',
                                  clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0 100%)'
                                }}
                              >
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 当前位置标记 */}
                        {isCurrentPosition && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 bg-yellow-500 rounded-full animate-pulse" />
                          </div>
                        )}
                        
                        {/* 坐标显示 */}
                        {numbersVisible && (
                          <span 
                            className="absolute inset-0 flex items-center justify-center text-xs font-mono text-gray-600 pointer-events-none select-none"
                            style={{ fontSize: '9px' }}
                          >
                            {actualRow},{col}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 使用说明 */}
            <div className={`mt-4 border-l-4 p-4 rounded ${
              mode === 'game' 
                ? 'bg-green-50 border-green-400' 
                : 'bg-blue-50 border-blue-400'
            }`}>
              <h3 className={`font-semibold mb-2 ${
                mode === 'game' ? 'text-green-800' : 'text-blue-800'
              }`}>
                {mode === 'game' ? '游戏规则' : '操作说明'}
              </h3>
              <ul className={`text-sm space-y-1 ${
                mode === 'game' ? 'text-green-700' : 'text-blue-700'
              }`}>
                {mode === 'game' ? (
                  <>
                    <li>• 初始状态：A堆10枚，B堆20枚硬币</li>
                    <li>• 每回合只能向<strong>左</strong>、<strong>下</strong>或<strong>左下</strong>方向移动</li>
                    <li>• 无法移动的玩家（到达(0,0)）获胜</li>
                    <li>• <strong>点击绿色高亮格子</strong>进行移动</li>
                  </>
                ) : (
                  <>
                    <li>• <strong>🎨 颜色模式</strong>: 点击标记危险区域</li>
                    <li>• <strong>鼠标悬停</strong>: 预览可移动区域</li>
                    <li>• 用不同颜色和旗帜分析多个位置的策略</li>
                  </>
                )}
                <li>• <strong>🚩 旗帜模式</strong>: 点击标记重要位置，方便参考</li>
                <li>• <strong>右键点击</strong>: 移除旗帜或颜色标记</li>
              </ul>
            </div>
          </div>

          {/* 硬币可视化区域 */}
          <div className="w-96">
            <div className="bg-white rounded-lg shadow-md border sticky top-6">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-800 text-center flex items-center justify-center gap-2">
                  <span>💰</span>
                  硬币状态
                  <span>💰</span>
                </h3>
              </div>
              
              <div className="p-4">
                {mode === 'game' ? (
                  <div className="space-y-4">
                    <div className="text-center text-sm text-gray-600 mb-4 bg-gray-50 p-2 rounded">
                      当前游戏位置: ({gameState.currentPosition.row}, {gameState.currentPosition.col})
                    </div>
                    
                    <div className="space-y-4">
                      {renderCoins(gameState.currentPosition.row, 'A堆 (金币)', 'gold')}
                      {renderCoins(gameState.currentPosition.col, 'B堆 (银币)', 'silver')}
                    </div>
                  </div>
                ) : hoveredCell ? (
                  <div className="space-y-4">
                    <div className="text-center text-sm text-gray-600 mb-4 bg-gray-50 p-2 rounded">
                      鼠标位置: ({hoveredCell.row}, {hoveredCell.col})
                    </div>
                    
                    <div className="space-y-4">
                      {renderCoins(hoveredCell.row, 'A堆 (金币)', 'gold')}
                      {renderCoins(hoveredCell.col, 'B堆 (银币)', 'silver')}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    <div className="text-4xl mb-4">🪙</div>
                    <p>将鼠标移动到网格上</p>
                    <p className="text-sm">查看对应的硬币状态</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
                  <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default WythoffGame;
